import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Youtube } from "lucide-react";
import { Logo } from "./Logo";

const links = [
  { label: "Top of Page", kind: "top" },
  { label: "Home", route: "/" },
  { label: "C408 Training", route: "/training/c408" },
  { label: "Contract Pilot", route: "/services/contract-pilot" },
  { label: "Gallery", anchor: "gallery" },
  { label: "Contact Us", anchor: "contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (anchor) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${anchor}`);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  };

  const handleTop = (e) => {
    e.preventDefault();
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderLink = (l, isMobile = false) => {
    const cls = isMobile
      ? "text-white/80 text-sm tracking-[0.2em] uppercase py-2"
      : "text-[11px] tracking-[0.28em] uppercase text-white/70 hover:text-white transition-colors duration-500";
    const testid = `${isMobile ? "mobile-" : ""}nav-link-${l.label
      .toLowerCase()
      .replace(/\s+/g, "-")}`;
    if (l.kind === "top") {
      return (
        <a
          key={l.label}
          href="#top"
          onClick={handleTop}
          className={cls}
          data-testid={testid}
        >
          {l.label}
        </a>
      );
    }
    if (l.route) {
      return (
        <Link
          key={l.label}
          to={l.route}
          onClick={() => setOpen(false)}
          className={cls}
          data-testid={testid}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.label}
        href={`/#${l.anchor}`}
        onClick={handleAnchor(l.anchor)}
        className={cls}
        data-testid={testid}
      >
        {l.label}
      </a>
    );
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/70 border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" className="text-white" data-testid="nav-brand">
          <Logo />
        </Link>
        <nav className="hidden md:flex items-center gap-8 lg:gap-10" aria-label="Menu List">
          <span className="hidden lg:inline-flex font-mono-label text-white/35 pr-2 border-r border-white/10 mr-2">
            Menu List
          </span>
          {links.map((l) => renderLink(l, false))}
        </nav>
        <a
          href="/#contact"
          onClick={handleAnchor("contact")}
          className="hidden md:inline-flex btn-outline btn-solid"
          data-testid="nav-cta"
        >
          Let&apos;s Fly
          <span aria-hidden>→</span>
        </a>
        <a
          href="https://www.youtube.com/@elelyonair"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="El Elyon Air on YouTube"
          data-testid="nav-youtube-link"
          className="hidden md:inline-flex items-center justify-center w-10 h-10 ml-3 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-[#FF5A00] hover:bg-[#FF5A00]/10 transition-colors duration-300"
        >
          <Youtube className="w-4 h-4" />
        </a>
        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
          data-testid="nav-toggle"
        >
          <div className="w-6 h-[1px] bg-white mb-1.5"></div>
          <div className="w-6 h-[1px] bg-white"></div>
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4" aria-label="Menu List">
              <div className="font-mono-label text-white/35 pb-2 border-b border-white/10 mb-2">
                Menu List
              </div>
              {links.map((l) => renderLink(l, true))}
              <a
                href="https://www.youtube.com/@elelyonair"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-youtube-link"
                className="mt-2 inline-flex items-center gap-3 text-white/80 text-sm tracking-[0.2em] uppercase py-2"
              >
                <Youtube className="w-4 h-4 text-[#FF5A00]" />
                YouTube · @elelyonair
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
